import { Link } from 'react-router-dom';
import { CalendarIcon, ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBlogFeaturedImage, useBlogCategories } from '@/hooks/useBlog';
import { blogService, WordPressPost } from '@/services/blogService';

interface BlogPostCardProps {
  post: WordPressPost;
}

const BlogPostCard = ({ post }: BlogPostCardProps) => {
  const { data: featuredImageUrl } = useBlogFeaturedImage(post);
  const { data: categories } = useBlogCategories();
  
  // Add defensive checks for post and its properties
  const postCategories = categories?.filter(cat => 
    post?.categories && Array.isArray(post.categories) && post.categories.includes(cat.id)
  ) || [];
  
  const contentText = post?.content?.rendered || '';
  const readingTime = contentText ? Math.ceil(blogService.stripHtml(contentText).split(' ').length / 200) : 1;

  return (
    <Card className="overflow-hidden group hover:shadow-lg transition-shadow">
      <Link to={`/blog/${post.slug}`} className="aspect-video overflow-hidden block cursor-pointer">
        <img
          src={featuredImageUrl || "/api/placeholder/400/250"}
          alt={post?.title?.rendered ? blogService.stripHtml(post.title.rendered) : ''}
          className="w-full h-full object-cover group-transition-transform duration-300 no-watercolor"
        />
      </Link>
      <CardHeader>
        {postCategories.length > 0 && (
          <Badge className="w-fit mb-2">{postCategories[0].name}</Badge>
        )}
        <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors">
          <Link to={`/blog/${post.slug}`}>
            {post?.title?.rendered ? blogService.stripHtml(post.title.rendered) : 'No Title'}
          </Link>
        </CardTitle>
        <CardDescription className="line-clamp-3">
          {post?.excerpt?.rendered ? blogService.stripHtml(post.excerpt.rendered) : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center text-sm text-muted-foreground mb-4">
          <CalendarIcon className="w-4 h-4 mr-2" />
          <span>{post?.date ? new Date(post.date).toLocaleDateString() : ''}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center">
            <ClockIcon className="w-4 h-4 mr-1" />
            {readingTime} min read
          </span>
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/blog/${post.slug}`}>
              Read More <ArrowRightIcon className="ml-1 w-3 h-3" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BlogPostCard;